import React from 'react';
import { Box } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { motion } from 'framer-motion';

// "What membership includes" — inclusions only, no exclusion language.
// Guests are always welcome regardless of membership; this list frames
// membership purely as what it adds, per the client's build notes.
//
// Two items carry client-locked exact copy from the wireframe/decisions
// email — the member-discount percentage (locked at 10%) and the "makom"
// (reserved seat) wording. Do not paraphrase these two.
//
// Rendered as a single drenched navy panel rather than plain text on the
// page background: this is the value proposition a visitor reads right
// before they hit the pricing tiers, and on an otherwise white page a
// dark block is what makes it register as the moment that matters. The
// makom line closes the panel under a hairline rule — it's the most
// personal item and reads better as a closing statement than as one more
// bullet competing with "key fob access".
//
// Order is client-specified (Aug 2026) — the two benefits that matter
// most during the membership drive (High Holiday seats, then first rights
// on signups) lead the list. Don't re-sort.
const INCLUSIONS = [
  '2 High Holiday seats',
  'First rights on all signups — High Holidays, Shabbatons, and events',
  '10% off — kiddushim, hall rental, and Shabbatons',
  'Key fob building access for the whole family',
  'Members-only chat and events',
  'A Rosh Hashanah member gift',
];

const FEATURED = 'Your makom — a reserved seat with your name, where your shtender stays.';

export default function InclusionsSection() {
  return (
    <Box
      sx={{
        maxWidth: 940,
        mx: 'auto',
        background: 'var(--navy-900)',
        px: { xs: 3, sm: 5, md: 7 },
        py: { xs: 4.5, sm: 6 },
        boxShadow: '0 40px 80px -50px rgba(7,6,26,0.6)',
      }}
    >
      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          rowGap: { xs: 2.5, sm: 3.25 },
          columnGap: { sm: 5, md: 6 },
        }}
      >
        {INCLUSIONS.map((item, i) => (
          <motion.li
            key={item}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}
          >
            <CheckRoundedIcon
              aria-hidden
              sx={{ fontSize: 22, color: 'var(--teal-300)', flexShrink: 0, mt: '1px' }}
            />
            <Box
              sx={{
                fontFamily: 'var(--font-body)',
                fontSize: { xs: 16.5, md: 18 },
                fontWeight: 500,
                color: '#fff',
                lineHeight: 1.5,
              }}
            >
              {item}
            </Box>
          </motion.li>
        ))}
      </Box>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.7, delay: INCLUSIONS.length * 0.06, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            mt: { xs: 4, sm: 5 },
            pt: { xs: 3, sm: 3.5 },
            borderTop: '1px solid rgba(255,255,255,0.16)',
            textAlign: 'center',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: { xs: 18, md: 21 },
            color: 'var(--teal-300)',
            lineHeight: 1.5,
          }}
        >
          {FEATURED}
        </Box>
      </motion.div>
    </Box>
  );
}
